import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { SITE } from "../data/site";
import { submitContact } from "../lib/api";

export const Contact = ({ onQuote, onMeeting }) => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email and message.");
            return;
        }
        setLoading(true);
        try {
            await submitContact(form);
            toast.success("Message sent. We'll be in touch shortly.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error("Could not send message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 lg:py-36 bg-[#1A1A1A] text-[#FAF9F6]"
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-12 gap-6 lg:gap-16">
                    {/* Left: heading + offices */}
                    <div className="col-span-12 lg:col-span-5">
                        <div className="overline text-[#FAF9F6]/70 mb-6">
                            Contact
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
                            Let&apos;s begin a<br />
                            <span className="italic text-[#C7B299]">conversation</span>.
                        </h2>
                        <p className="mt-8 text-[#FAF9F6]/70 max-w-md leading-relaxed font-light">
                            Tell us about your space. We respond to every enquiry within
                            one working day.
                        </p>

                        <div className="mt-12 flex flex-wrap gap-3">
                            <button
                                data-testid="contact-quote-btn"
                                onClick={onQuote}
                                className="px-7 py-4 text-xs uppercase tracking-[0.18em] font-medium bg-[#FAF9F6] text-[#1A1A1A] hover:bg-[#C7B299] transition-colors"
                            >
                                Get a Quote
                            </button>
                            <button
                                data-testid="contact-meeting-btn"
                                onClick={onMeeting}
                                className="px-7 py-4 text-xs uppercase tracking-[0.18em] font-medium border border-[#FAF9F6]/40 text-[#FAF9F6] hover:bg-[#FAF9F6] hover:text-[#1A1A1A] transition-colors"
                            >
                                Book Meeting
                            </button>
                        </div>

                        <div className="mt-16 space-y-10">
                            {SITE.offices.map((o) => (
                                <div key={o.label} data-testid={`office-${o.label}`}>
                                    <div className="overline text-[#C7B299] mb-3 flex items-center gap-2">
                                        <MapPin size={14} strokeWidth={1.5} />
                                        {o.label}
                                    </div>
                                    <p className="text-[#FAF9F6]/85 leading-relaxed font-light max-w-sm">
                                        {o.address}
                                    </p>
                                </div>
                            ))}

                            <div className="pt-2 space-y-3">
                                <a
                                    href={`mailto:${SITE.email}`}
                                    data-testid="contact-email-link"
                                    className="flex items-center gap-3 text-[#FAF9F6]/85 hover:text-[#C7B299] transition-colors"
                                >
                                    <Mail size={16} strokeWidth={1.5} />
                                    <span className="font-light">{SITE.email}</span>
                                </a>
                                {SITE.phones.map((p) => (
                                    <a
                                        key={p}
                                        href={`tel:${p.replace(/\s/g, "")}`}
                                        data-testid={`contact-phone-${p}`}
                                        className="flex items-center gap-3 text-[#FAF9F6]/85 hover:text-[#C7B299] transition-colors"
                                    >
                                        <Phone size={16} strokeWidth={1.5} />
                                        <span className="font-light">{p}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="col-span-12 lg:col-span-6 lg:col-start-7">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"
                        >
                            <Field
                                label="Your name"
                                value={form.name}
                                onChange={onChange("name")}
                                testid="contact-name"
                            />
                            <Field
                                label="Email"
                                type="email"
                                value={form.email}
                                onChange={onChange("email")}
                                testid="contact-email-input"
                            />
                            <Field
                                label="Phone"
                                value={form.phone}
                                onChange={onChange("phone")}
                                testid="contact-phone-input"
                            />
                            <Field
                                label="Subject"
                                value={form.subject}
                                onChange={onChange("subject")}
                                testid="contact-subject"
                            />
                            <div className="sm:col-span-2">
                                <label className="dd-label text-[#FAF9F6]/60">Message</label>
                                <textarea
                                    data-testid="contact-message"
                                    value={form.message}
                                    onChange={onChange("message")}
                                    rows={5}
                                    className="dd-input"
                                    style={{
                                        color: "#FAF9F6",
                                        borderBottomColor: "rgba(250,249,246,0.25)",
                                    }}
                                    placeholder="Tell us about your project..."
                                />
                            </div>
                            <div className="sm:col-span-2 mt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit"
                                    className="px-9 py-4 text-xs uppercase tracking-[0.18em] font-medium bg-[#FAF9F6] text-[#1A1A1A] hover:bg-[#C7B299] transition-colors disabled:opacity-50"
                                >
                                    {loading ? "Sending..." : "Send message"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, value, onChange, type = "text", testid }) => (
    <div>
        <label className="dd-label text-[#FAF9F6]/60">{label}</label>
        <input
            data-testid={testid}
            type={type}
            value={value}
            onChange={onChange}
            className="dd-input"
            style={{
                color: "#FAF9F6",
                borderBottomColor: "rgba(250,249,246,0.25)",
            }}
        />
    </div>
);
